import React from 'react';
import { COLORS, BUTTON_TOKENS, BORDER_RADIUS } from '../../../styles/UISystem';

/**
 * IconCircleButton Component
 *
 * A reusable circular button wrapper for Phosphor icons.
 * Used in headers, chat inputs, and action buttons throughout the app.
 *
 * Features:
 * - Circular white background with semi-transparency
 * - Subtle drop shadow for visibility on white backgrounds
 * - Works with any Phosphor icon component
 * - Can be a button (with onClick) or link (with href)
 * - Customizable sizing and colors
 * - Supports selected state (light gray background)
 *
 * @param {Object} props
 * @param {Component} props.icon - Phosphor icon component (e.g., Plus, Phone)
 * @param {number} props.size - Icon size in pixels (default: 24)
 * @param {function} props.onClick - Click handler (makes it a button)
 * @param {string} props.href - URL for link (makes it an anchor)
 * @param {number} props.buttonSize - Size of circular background (default: 44)
 * @param {string} props.backgroundColor - Background color (default: semi-transparent white)
 * @param {string} props.iconColor - Icon color (default: DARK_GRAY)
 * @param {string} props.iconWeight - Phosphor icon weight (default: 'regular')
 * @param {boolean} props.isSelected - Selected state (light gray background when true)
 * @param {boolean} props.inline - Inline/decorative mode: no shadow, no cursor (for use inside a larger tap target)
 * @param {string} props.variant - Visual variant: 'default' (semi-transparent, TIER1) or 'prominent' (opaque white, TIER2 — back button style)
 *
 * @example
 * // As a button
 * <IconCircleButton icon={Plus} onClick={handleClick} />
 *
 * @example
 * // As a link
 * <IconCircleButton icon={InstagramLogo} href="https://instagram.com/user" />
 *
 * @example
 * // Custom sizing
 * <IconCircleButton icon={Phone} size={20} buttonSize={36} onClick={handleCall} />
 *
 * @example
 * // Inline (decorative icon inside a larger clickable container)
 * <IconCircleButton icon={Phone} inline buttonSize={28} size={14} />
 */
export const IconCircleButton = ({
  icon: Icon,
  size = 24,
  onClick,
  href,
  buttonSize = 44,
  backgroundColor,
  iconColor = COLORS.DARK_GRAY,
  iconWeight = 'regular',
  isSelected = false,
  inline = false,
  variant = 'default',
  ...props
}) => {
  // Determine background color: explicit prop > variant > selected state
  const isProminent = variant === 'prominent';
  const variantBgColor = isProminent ? BUTTON_TOKENS.BACK_BG : BUTTON_TOKENS.CIRCLE_BG;
  const defaultBgColor = isSelected ? COLORS.MEDIUM_GRAY : variantBgColor;
  const finalBgColor = backgroundColor || defaultBgColor;

  // Shadow: inline = none, prominent = TIER2, default = TIER1
  const shadow = inline
    ? 'none'
    : isProminent
      ? BUTTON_TOKENS.BACK_SHADOW
      : BUTTON_TOKENS.CIRCLE_SHADOW;

  const buttonStyle = {
    width: `${buttonSize}px`,
    height: `${buttonSize}px`,
    borderRadius: BORDER_RADIUS.CIRCLE,
    backgroundColor: finalBgColor,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: inline ? 'default' : 'pointer',
    flexShrink: 0,
    boxShadow: shadow,
    border: 'none',
    padding: 0,
    textDecoration: 'none',
    transition: inline ? 'none' : 'background-color 0.2s ease',
  };

  // If href is provided, render as a link
  if (href) {
    return (
      <a href={href} style={buttonStyle} {...props}>
        <Icon size={size} color={iconColor} weight={iconWeight} />
      </a>
    );
  }

  // Otherwise render as a button (div with onClick)
  return (
    <div onClick={onClick} style={buttonStyle} {...props}>
      <Icon size={size} color={iconColor} weight={iconWeight} />
    </div>
  );
};

export default IconCircleButton;
